import { authService } from '../../../services/auth.ts';
import { OrganizationService } from '../../../services/organization.ts';
import { GATEWAY_MODULES } from '../../../models/modules.ts';
import { SubNav } from '../../../components/library/ui/sub-nav.ts';
import '../../../components/library/ui/app-button.ts';

// Tab components
import './tabs/plugins.ts';
import './tabs/history.ts';
import './tabs/metrics.ts';
import './tabs/domains.ts';
import './tabs/nodes.ts';

// UI Components for modals/editors
import '../../../components/library/ui/app-modal.ts';
import '../../../components/library/ui/code-editor.ts';
import { ToastService } from '../../../services/toast.ts';

export class AppGateway extends HTMLElement {
  private _currentSubView: string = 'metrics';
  private _hasAccess: boolean = false;
  private _hasByoc: boolean = false;
  private _hasDedicated: boolean = false;
  private _hasShared: boolean = false;
  private _isLoading: boolean = true;
  private _orgService = OrganizationService.getInstance();
  private _configHistory: any[] = [];
  private _orgId: string = '';
  private _breadcrumb: string | null = null;
  private _subNav: SubNav | null = null;
  private _toast = ToastService.getInstance();

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['org-id', 'tab'];
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (name === 'org-id' && oldVal !== newVal) {
      console.debug('AppGateway: Organization ID changed to:', newVal);
      this._orgId = newVal;
      if (this.isConnected) {
        this.checkAccess();
      }
    }
    if (name === 'tab' && oldVal !== newVal) {
      console.debug('AppGateway: Tab changed to:', newVal);
      this.setSubView(newVal || 'metrics');
    }
  }

  async connectedCallback() {
    const user = authService.getUser();
    this._orgId = user?.org_id || localStorage.getItem('crate_org_id') || '';

    const initialTab = this.getAttribute('tab');
    if (initialTab) {
      this._currentSubView = initialTab;
    }

    await this.checkAccess();
    requestAnimationFrame(() => this.emitPanel());
  }

  private async checkAccess() {
    this._isLoading = true;
    this.render();

    try {
      // Final fallback to profile if still missing
      if (!this._orgId) {
        console.debug('AppGateway: Org ID missing, fetching profile...');
        const profile = await authService.getProfile();
        this._orgId = profile?.org_id || '';
      }

      if (!this._orgId) {
        console.warn('AppGateway: No organization context found');
        this._hasAccess = false;
        return;
      }

      const subscription = await this._orgService.getSubscription(this._orgId);
      const modules = subscription?.modules || [];
      this._hasAccess = modules.some((m: any) => {
        const id = typeof m === 'object' ? m.id : m;
        return GATEWAY_MODULES.includes(id);
      });
      this._hasByoc = modules.some((m: any) => {
        const id = typeof m === 'object' ? m.id : m;
        return id === 'byoc_gateway';
      });
      this._hasDedicated = modules.some((m: any) => {
        const id = typeof m === 'object' ? m.id : m;
        return id === 'dedicated_gateway';
      });
      this._hasShared = modules.some((m: any) => {
        const id = typeof m === 'object' ? m.id : m;
        return id === 'shared_gateway';
      });
    } catch (e) {
      console.error('Failed to check gateway access', e);
      this._hasAccess = false;
    } finally {
      this._isLoading = false;
      this.render();
      this.emitPanel();
    }
  }

  private async loadHistory() {
    try {
      const historyRes = await this._orgService.getConfigHistory(this._orgId);
      this._configHistory = (historyRes as any).entries || historyRes || [];
    } catch (e) {
      console.error('Failed to load config history', e);
    }
  }

  async setSubView(view: string) {
    if (this._currentSubView === view) return;
    this._currentSubView = view;
    this._breadcrumb = null;

    if (view === 'history') {
      await this.loadHistory();
    }

    this.render();
    this.emitPanel();
  }

  private emitPanel() {
    if (!this._hasAccess) {
      this.dispatchEvent(new CustomEvent('shell-panel-changed', {
        detail: null, bubbles: true, composed: true
      }));
      return;
    }
    const views = [
      { id: 'metrics', name: 'Metrics', icon: 'bar-chart' },
      { id: 'domains', name: 'Domains', icon: 'globe' },
      { id: 'plugins', name: 'Plugins', icon: 'plugin' },
      { id: 'history', name: 'History', icon: 'history' }
    ];

    if (this._hasAccess) {
      views.push({ id: 'nodes', name: 'Nodes', icon: 'server' });
    }

    const viewMeta: Record<string, { title: string, subtitle: string }> = {
      'metrics': { title: 'API Gateway', subtitle: 'Metrics' },
      'domains': { title: 'API Gateway', subtitle: '' },
      'plugins': { title: 'API Gateway', subtitle: 'Plugins' },
      'history': { title: 'API Gateway', subtitle: 'History' }
    };

    this._subNav = document.createElement('app-sub-nav') as SubNav;
    this._subNav.title = 'Gateway';
    this._subNav.items = views;
    this._subNav.activeId = this._currentSubView;

    this._subNav.addEventListener('nav-change', ((e: CustomEvent) => {
      const tab = e.detail.id;
      window.dispatchEvent(new CustomEvent('navigate', { 
        detail: { view: 'gateway', params: { tab } } 
      }));
    }) as any);

    this.updateShellHeader();
  }

  private updateShellHeader() {
    const viewMeta: Record<string, { title: string, subtitle: string }> = {
      'metrics': { title: 'API Gateway', subtitle: 'Metrics' },
      'domains': { title: 'API Gateway', subtitle: '' },
      'plugins': { title: 'API Gateway', subtitle: 'Plugins' },
      'history': { title: 'API Gateway', subtitle: 'History' },
      'nodes': { title: 'API Gateway', subtitle: 'Nodes' }
    };

    const meta = viewMeta[this._currentSubView] || { title: 'Gateway', subtitle: '' };

    this.dispatchEvent(new CustomEvent('shell-panel-changed', {
      detail: { 
        panel: this._subNav,
        header: {
          title: this._breadcrumb ? `<a href="#gateway${this._orgId ? '/' + this._orgId : ''}/domains" style="color: inherit; text-decoration: none;">Domains</a>` : meta.title,
          subtitle: this._breadcrumb ? `<span style="color: var(--text-dim); margin: 0 8px;">/</span>${this._breadcrumb}<span style="color: var(--text-dim); margin: 0 8px;">/</span>Rules` : (meta.subtitle || '')
        }
      }, 
      bubbles: true, 
      composed: true 
    }));
  }

  private setBreadcrumb(host: string | null) {
    this._breadcrumb = host;
    this.updateShellHeader();
  }

  // ── View Content ─────────────────────────────────────────

  private updateTabContent() {
    const container = this.querySelector('.gateway-content');
    if (!container) return;

    const currentTab = container.firstElementChild;
    const expectedTag = this.getExpectedTabTag();

    // Only replace if the tab type has actually changed
    if (!currentTab || currentTab.tagName.toLowerCase() !== expectedTag) {
      container.innerHTML = this.getViewContent();
    } else {
      // Just update attributes if it's the same tab type
      currentTab.setAttribute('org-id', this._orgId);
      if (expectedTag === 'gateway-nodes-tab') {
        currentTab.setAttribute('has-byoc', String(this._hasByoc));
        currentTab.setAttribute('has-dedicated', String(this._hasDedicated));
        currentTab.setAttribute('has-shared', String(this._hasShared));
      }
      if (expectedTag === 'gateway-history-tab') {
        (currentTab as any).configs = this._configHistory;
      }
    }
  }

  private getExpectedTabTag(): string {
    switch (this._currentSubView) {
      case 'domains': return 'gateway-domains-tab';
      case 'plugins': return 'gateway-plugins-tab';
      case 'history': return 'gateway-history-tab';
      case 'nodes': return 'gateway-nodes-tab';
      case 'metrics': return 'gateway-metrics';
      default: return 'gateway-metrics';
    }
  }

  private getViewContent(): string {
    switch (this._currentSubView) {
      case 'domains':
        return `<gateway-domains-tab org-id="${this._orgId}"></gateway-domains-tab>`;
      case 'plugins':
        return `<gateway-plugins-tab org-id="${this._orgId}"></gateway-plugins-tab>`;
      case 'history':
        return `<gateway-history-tab id="history-tab"></gateway-history-tab>`;
      case 'nodes':
        return `<gateway-nodes-tab org-id="${this._orgId}" has-byoc="${this._hasByoc}" has-dedicated="${this._hasDedicated}" has-shared="${this._hasShared}"></gateway-nodes-tab>`;
      case 'metrics':
        return `<gateway-metrics org-id="${this._orgId}"></gateway-metrics>`;
      default:
        return `<gateway-metrics org-id="${this._orgId}"></gateway-metrics>`;
    }
  }

  // ── Render ───────────────────────────────────────────────

  render() {
    if (this._isLoading) {
      this.innerHTML = `<div style="padding: 40px; color: var(--text-muted);">Loading...</div>`;
      return;
    }

    if (!this._hasAccess) {
      this.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: calc(100vh - 100px); gap: 24px; padding: 40px; text-align: center;">
          <div class="sketchy-card" style="max-width: 450px; padding: 40px; background: rgba(15, 23, 42, 0.4); border: 1px dashed rgba(255,255,255,0.1); border-radius: 12px;">
            <div style="position: relative; z-index: 2;">
              <app-icon name="lock" size="48" style="color: var(--text-muted); margin-bottom: 20px; opacity: 0.8;"></app-icon>
              <h1 style="font-family: var(--font-main); font-size: 1.5rem; margin-bottom: 12px; color: var(--text-main); font-weight: 500;">Module Locked</h1>
              <p style="font-family: var(--font-main); color: var(--text-dim); line-height: 1.5; margin-bottom: 28px; font-size: 0.95rem;">
                Your organization does not have the API Gateway module enabled.
                Enable it in your organization settings to start managing your API traffic.
              </p>
              <app-button variant="primary" size="sm" id="btn-enable-gateway" style="width: 100%;">
                Enable Module
              </app-button>
            </div>
          </div>
        </div>
      `;
      this.querySelector('#btn-enable-gateway')?.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'organization', params: { tab: 'billing' } } }));
      });
      return;
    }

    const existingContainer = this.querySelector('.gateway-container');
    if (!existingContainer) {
      this.innerHTML = `
        <style>
          .gateway-container {
            padding: 0;
            max-width: 1200px;
            margin: 0 auto;
          }
          .sketchy-card {
            position: relative;
            background: rgba(255,255,255,0.01);
            padding: 32px;
            transition: all 0.2s ease;
            border: 1px solid var(--border);
            border-radius: 10px;
            --border-sketch: var(--border);
          }
          .sketchy-card.interactive {
            cursor: pointer;
          }
          .sketchy-card.interactive:hover {
            background: rgba(255,255,255,0.03);
            transform: translateY(-4px);
            border-color: var(--accent);
            --border-sketch: var(--accent);
            box-shadow: 0 4px 20px rgba(245, 158, 11, 0.08);
          }
          .loader {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(245, 158, 11, 0.1);
            border-top-color: var(--accent, #f59e0b);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 2rem auto;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>

        <div class="gateway-container">
          <div class="gateway-content">
            <!-- Populated by updateTabContent -->
          </div>
        </div>
      `;
    }

    this.updateTabContent();

    this.bindEvents();
  }

  private bindEvents() {
    // Listen for tab navigation from child components (overview cards, routing empty state)
    this.addEventListener('navigate-tab', ((e: CustomEvent) => {
      const tab = e.detail?.tab;
      if (tab) this.setSubView(tab);
    }) as any);

    // History tab data wiring
    const historyTab = this.querySelector('#history-tab') as any;
    if (historyTab) {
      historyTab.configs = this._configHistory;

      // Listen for history actions
      historyTab.addEventListener('view-config', (e: CustomEvent) => this.handleViewConfig(e.detail));
      historyTab.addEventListener('rollback-config', (e: CustomEvent) => this.handleRollbackConfig(e.detail));
      historyTab.addEventListener('export-config', () => this.handleExportConfig());
      historyTab.addEventListener('import-config', (e: CustomEvent) => this.handleImportConfig(e.detail.content));
      historyTab.addEventListener('view-current', () => this.handleViewCurrent());
    }

    // Breadcrumb listener
    const domainsTab = this.querySelector('gateway-domains-tab');
    if (domainsTab) {
      domainsTab.addEventListener('breadcrumb-change', ((e: CustomEvent) => {
        this.setBreadcrumb(e.detail);
      }) as any);
    }
  }

  // ── History Handlers ─────────────────────────────────────

  private async handleViewConfig(id: string) {
    try {
      const config = await this._orgService.getConfigHistoryEntry(this._orgId, id);
      this.showConfigEditor(`Historical Configuration (v${config.version || id.substring(0, 8)})`, config.config_yaml || config.config || config.yaml || config.content || '', false);
    } catch (e) {
      // Toast handled by ApiClient
    }
  }

  private async handleViewCurrent() {
    try {
      const config = await this._orgService.getConfig(this._orgId);
      this.showConfigEditor('Current Configuration', config, true);
    } catch (e) {
      // Toast handled by ApiClient
    }
  }

  private async handleRollbackConfig(id: string) {
    if (!confirm('Are you sure you want to rollback to this version? This will overwrite your current settings.')) {
      return;
    }

    try {
      await this._orgService.rollbackConfig(this._orgId, id);
      await this.loadHistory();
      this.render();
    } catch (e) {
      // Toast handled by ApiClient
    }
  }

  private async handleExportConfig() {
    try {
      const config = await this._orgService.getConfig(this._orgId);
      const blob = new Blob([config], { type: 'application/x-yaml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gateway-config-${this._orgId}-${new Date().toISOString().split('T')[0]}.yaml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      this._toast.success('Configuration exported successfully');
    } catch (e) {
      // Toast handled by ApiClient
    }
  }

  private async handleImportConfig(content: string) {
    try {
      await this._orgService.updateConfig(this._orgId, content);
      await this.loadHistory();
      this.render();
    } catch (e) {
      // Toast handled by ApiClient
    }
  }

  private showConfigEditor(title: string, content: string, editable: boolean) {
    const modal = document.createElement('app-modal') as any;
    modal.setAttribute('size', 'lg');
    modal.setAttribute('open', '');
    
    modal.innerHTML = `
      <div slot="header">${title}</div>
      <div style="height: 60vh; min-height: 400px; margin-bottom: 1rem;">
        <code-editor id="config-editor-in-modal"></code-editor>
      </div>
      <div slot="actions">
        <div style="display: flex; gap: 0.5rem; justify-content: flex-end; width: 100%;">
          <app-button variant="ghost" size="sm" id="modal-cancel-btn" icon="x" data-tooltip="Close"></app-button>
          ${editable ? `<app-button variant="primary" size="sm" id="modal-save-btn" icon="check" data-tooltip="Save Changes"></app-button>` : ''}
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    
    const editor = modal.querySelector('#config-editor-in-modal');
    // We need to wait a tick for the shadow root to be ready in the editor if it was just added
    requestAnimationFrame(() => {
      if (editor) editor.value = content;
    });
    
    modal.querySelector('#modal-cancel-btn').addEventListener('click', () => {
      modal.removeAttribute('open');
      setTimeout(() => document.body.removeChild(modal), 300);
    });

    if (editable) {
      const saveBtn = modal.querySelector('#modal-save-btn');
      saveBtn.addEventListener('click', async () => {
        try {
          saveBtn.setAttribute('loading', '');
          await this._orgService.updateConfig(this._orgId, editor.value);
          modal.removeAttribute('open');
          setTimeout(() => document.body.removeChild(modal), 300);
          await this.loadHistory();
          this.render();
        } catch (e) {
          // Toast handled by ApiClient
        } finally {
          saveBtn.removeAttribute('loading');
        }
      });
    }
  }
}

if (!customElements.get('app-gateway')) {
  customElements.define('app-gateway', AppGateway);
}
